import React, { forwardRef, useState, useEffect } from 'react';
import flight from '../assets/material-symbols_flight.svg';
import hotel from '../assets/fluent_building-32-filled.svg';
import luggage from '../assets/travel-svgrepo-com.png';
import health from '../assets/health-svgrepo-com.png';
import taxi from '../assets/bxs_taxi.svg';
import vector from '../assets/Vector (5).svg';
import durationIcon from '../assets/duration.svg (1).svg';
import meal from '../assets/meal-svgrepo-com.png';
import calendar from '../assets/simple-line-icons_calender.svg';
import location from '../assets/locations-svgrepo-com.png';
import earth from '../assets/mdi_earth.svg';

import '../styles/Overview.css';

const Overview = forwardRef(({ tourId }, ref) => {
  // Default params
  const totalGuests = 2;
  const departure = 'New Delhi';
  const today = new Date().toISOString().split('T')[0]; // yyyy-mm-dd

  // Mock data fallback
  const mockData = {
    id: 3,
    name: '7-Day Italian Odyssey',
    description:
      'Experience the timeless charm of Italy on a 7-day journey through historic cities, exquisite cuisine, and unforgettable art.',
    duration_days: 7,
    duration_nights: 6,
    best_months_to_visit: 'April, May, October',
    countries_covered: 'France, Italy',
    cities_covered: 'Paris, Rome, Venice',
    price: 35449.65,
    discounted_price: 32613.678,
    is_visa_included: false,
    is_flight_included: true,
    is_insurance_included: true,
    flights: { total_flights: 4 },
    transfers: { total_transfers: 4 },
    hotels: {
      total_hotels: 3,
      hotels: [
        {
          name: 'Rome Grand Hotel',
          city: 'Rome',
          country: 'Italy',
          hotel_type: 'Deluxe',
          is_meal_included: false,
          meal_type: ['None'],
        },
        {
          name: 'Florence Boutique Inn',
          city: 'Florence',
          country: 'Italy',
          hotel_type: 'Boutique',
          is_meal_included: false,
          meal_type: ['None'],
        },
        {
          name: 'Venice Grand Hotel',
          city: 'Venice',
          country: 'Italy',
          hotel_type: 'Luxury',
          is_meal_included: true,
          meal_type: ['Breakfast'],
        },
      ],
    },
    activities: { total_activities: 10 },
  };

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const baseUrl = process.env.REACT_APP_BASE_URL || 'http://your-base-url.com';
        // Construct API URL with default params and passed tourId
        const url = `${baseUrl}/api/tour/${tourId}/overview?start_date=${today}&total_guests=${totalGuests}&include_flight=true&departure=${encodeURIComponent(
          departure
        )}`;

        const res = await fetch(url);

        if (!res.ok) {
          throw new Error('Failed to fetch overview data');
        }

        const jsonData = await res.json();
        setData(jsonData);
      } catch (err) {
        console.error(err);
        setError('Error fetching data, using mock data');
        setData(mockData);
      }
    };

    fetchOverview();
  }, [tourId]);

  if (!data) return <div>Loading overview...</div>;

  // Prepare overview data display
  const flights = data.flights?.total_flights ?? 0;
  const hotels = data.hotels?.total_hotels ?? 0;
  const transfers = data.transfers?.total_transfers ?? 0;
  const activities = data.activities?.total_activities ?? 0;
  const durationDays = data.duration_days ?? 'N/A';
  const durationNights = data.duration_nights ?? 'N/A';
  const visaIncluded = data.is_visa_included ? 'Included' : 'Not Included';
  const flightIncluded = data.is_flight_included ? 'Included' : 'Not Included';
  const insuranceIncluded = data.is_insurance_included ? 'Included' : 'Not Included';

  // Calculate meals - count how many hotels include meals and which meal types (simple example)
  let mealsCount = 0;
  let mealTypes = new Set();
  if (data.hotels?.hotels) {
    data.hotels.hotels.forEach((hotel) => {
      if (hotel.is_meal_included) {
        mealsCount++;
        hotel.meal_type.forEach((m) => mealTypes.add(m));
      }
    });
  }

  return (
    <div className="overview" ref={ref} style={{ marginLeft: '20px', marginRight: '20px' }}>
      <div className="overview-header">
        <div className="overview-title">Overview</div>
          <div className="overview-data">
            <div className="overview-data-1">
              <div className="overview-data-text">
                <img src={flight} alt="Flight" /> Flights : <span className="icon-data">{flights}</span>
              </div>
              <div className="overview-data-text">
                <img src={hotel} alt="Hotel" /> Accommodation : <span className="icon-data">{hotels}</span>
              </div>
              <div className="overview-data-text">
                <img src={meal} alt="Meal" /> Meals Included :{' '}
                <span className="icon-data">
                  {mealsCount} ({[...mealTypes].join(', ') || 'None'})
                </span>
              </div>
              <div className="overview-data-text">
                <img src={health} alt="Insurance" /> Travel Insurance :{' '}
                <span className="icon-data">{insuranceIncluded}</span>
              </div>
            </div>
            <div className="overview-data-2">
              <div className="overview-data-text">
                <img src={taxi} alt="Transfers" /> Transfers : <span className="icon-data">{transfers}</span>
              </div>
              <div className="overview-data-text">
                <img src={vector} alt="Activities" /> Activities : <span className="icon-data">{activities}</span>
              </div>
              <div className="overview-data-text">
                <img src={durationIcon} alt="Duration" /> Duration :{' '}
                <span className="icon-data">
                  {durationDays} Days / {durationNights} Nights
                </span>
              </div>
              <div className="overview-data-text">
                <img src={luggage} alt="Visa" /> Visa : <span className="icon-data">{visaIncluded}</span>
              </div>
            </div>
          </div>
        <div className="line"></div>
        <div className="overview-data">
          <div className="overview-data-1">
            <div className="overview-data-text">
              <img src={calendar} alt="Calendar" /> Best Months To Visit :{' '}
              <span className="icon-data">{data.best_months_to_visit || 'N/A'}</span>
            </div>
            <div className="overview-data-text">
              <img src={location} alt="Location" /> Cities Covered :{' '}
              <span className="icon-data">{data.cities_covered || 'N/A'}</span>
            </div>
          </div>
          <div className="overview-data-2">
            <div className="overview-data-text">
              <img src={earth} alt="Countries" /> Countries Covered :{' '}
              <span className="icon-data">{data.countries_covered || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default Overview;
